import { Capacitor } from '@capacitor/core';
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  type UserCredential,
} from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';

// ── Google ────────────────────────────────────────────────────────────────────

export async function signInWithGoogle(): Promise<UserCredential> {
  if (!Capacitor.isNativePlatform()) {
    try {
      logger.error('[google:web] signInWithPopup start');
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      const result = await signInWithPopup(auth, provider);
      logger.error('[google:web] signInWithPopup ok', { uid: result.user.uid });
      return result;
    } catch (err) {
      logger.error('[google:web] failed', err);
      throw err;
    }
  }

  // 네이티브(iOS): WKWebView는 popup/redirect 모두 차단 → 네이티브 GoogleSignIn으로
  // idToken만 받아 웹 Firebase SDK에 signInWithCredential (skipNativeAuth=true).
  try {
    logger.error('[google:native] signInWithGoogle start');
    const nativeResult = await FirebaseAuthentication.signInWithGoogle();
    const idToken = nativeResult.credential?.idToken;
    logger.error('[google:native] signInWithGoogle ok', { hasIdToken: !!idToken });

    if (!idToken) {
      throw new Error('Google idToken이 비어 있습니다. 다시 시도해 주세요.');
    }

    const credential = GoogleAuthProvider.credential(idToken);
    logger.error('[google:native] firebase signInWithCredential start');
    const result = await signInWithCredential(auth, credential);
    logger.error('[google:native] firebase ok', { uid: result.user.uid });
    return result;
  } catch (err) {
    logger.error('[google:native] failed', err);
    throw err;
  }
}

// ── Apple ────────────────────────────────────────────────────────────────────

function generateNonce(length = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (const byte of array) {
    result += chars[byte % chars.length];
  }
  return result;
}

async function sha256Hex(plain: string): Promise<string> {
  const encoded = new TextEncoder().encode(plain);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const APPLE_NATIVE_CLIENT_ID = 'com.mtmd.ledger'; // iOS Bundle ID
// 네이티브에선 redirectURI가 무시되지만 SignInWithApple 타입상 필수.
const APPLE_NATIVE_REDIRECT_URI = 'https://ledger-household-app.web.app';

export async function signInWithApple(): Promise<UserCredential> {
  try {
    logger.error('[apple] generateNonce start');
    const rawNonce = generateNonce();
    const hashedNonce = await sha256Hex(rawNonce);

    const isNative = Capacitor.isNativePlatform();
    let clientId: string;
    let redirectURI: string;

    if (isNative) {
      clientId = APPLE_NATIVE_CLIENT_ID;
      redirectURI = APPLE_NATIVE_REDIRECT_URI;
    } else {
      // 웹: Apple Developer "Service ID" + 등록된 Return URL이 필요
      const serviceId = import.meta.env.VITE_APPLE_SERVICE_ID as string | undefined;
      const webRedirectUri = import.meta.env.VITE_APPLE_REDIRECT_URI as string | undefined;
      if (!serviceId || !webRedirectUri) {
        throw new Error(
          '웹 Apple 로그인 설정 누락: VITE_APPLE_SERVICE_ID / VITE_APPLE_REDIRECT_URI 환경변수가 필요합니다.'
        );
      }
      clientId = serviceId;
      redirectURI = webRedirectUri;
    }

    logger.error('[apple] authorize start', { isNative, clientId });
    const { response } = await SignInWithApple.authorize({
      clientId,
      redirectURI,
      scopes: 'email name',
      nonce: hashedNonce,
    });
    logger.error('[apple] authorize ok', {
      hasIdentityToken: !!response?.identityToken,
    });

    if (!response?.identityToken) {
      throw new Error('Apple identityToken이 비어 있습니다. 다시 시도해 주세요.');
    }

    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: response.identityToken,
      rawNonce,
    });

    logger.error('[apple] firebase signInWithCredential start');
    const result = await signInWithCredential(auth, credential);
    logger.error('[apple] firebase ok', { uid: result.user.uid });
    return result;
  } catch (err) {
    logger.error('[apple] failed', err);
    throw err;
  }
}
